import type { AppLang } from '../../core/lang.service';
import { ApiError } from '../api/api-error';
import type { Banner } from '../models/catalog/banner.model';
import type { Catalog } from '../models/catalog/catalog.model';
import type { Category } from '../models/catalog/category.model';
import type {
  ModificationGroup,
  ModificationItem,
  Product,
  ProductVariant,
} from '../models/catalog/product.model';
import type { Promos } from '../models/catalog/promo.model';
import { isRecord, toArray, toId, toNumber, toText } from '../../shared/utils/parse';
import { NO_PROMOS } from '../../shared/utils/promo';

/**
 * Бэкенд шлёт обе локали разом: базовое поле — «что ввёл мерчант» (де-факто uz), `*Ru` — русское.
 * Fallback двусторонний: любая из сторон может быть не заполнена — пустую подменяем другой.
 */
function toLocalizedText(
  entry: Record<string, unknown>,
  base: string,
  lang: AppLang,
): string | null {
  const ru = toText(entry[`${base}Ru`]);
  const uz = toText(entry[base]);
  return lang === 'ru' ? (ru ?? uz) : (uz ?? ru);
}

/** 0 и null у лимитов — «без ограничения»: как «ноль штук» их читать нельзя, выбор стал бы невозможен. */
function toLimit(value: unknown): number | null {
  const limit = toNumber(value);
  return limit !== null && limit > 0 ? limit : null;
}

/** URL без trim, в отличие от toText: обрезка сменила бы, какую иконку категории чип сочтёт SVG. */
function toUrl(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function mapCategories(raw: unknown[], lang: AppLang): Category[] {
  const categories: Category[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) continue;
    const id = toId(entry['id']);
    const name = toLocalizedText(entry, 'name', lang);
    if (id === null || name === null) continue;
    categories.push({
      id,
      name,
      // Порядка сервер не присылает — его роль играет позиция в ответе.
      sortOrder: categories.length,
      // Инлайновой иконки с бэкенда нет — приходит `iconUrl`; `icon` — только у синтетических чипов «Все» и «Акции».
      icon: null,
      iconUrl: toUrl(entry['iconUrl']),
    });
  }
  return categories;
}

function mapItems(raw: unknown[], groupId: string, lang: AppLang): ModificationItem[] {
  const items: ModificationItem[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) continue;
    const id = toId(entry['id']);
    const name = toLocalizedText(entry, 'name', lang);
    const price = toNumber(entry['price']);
    if (id === null || name === null || price === null) continue;
    // groupId берём у родителя: принадлежность задаёт вложенность, а не поле элемента.
    items.push({ id, groupId, name, price, maxOrderSelect: toLimit(entry['maxOrderSelect']) });
  }
  return items;
}

function mapGroups(raw: unknown[], lang: AppLang): ModificationGroup[] {
  const groups: ModificationGroup[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) continue;
    const id = toId(entry['id']);
    const name = toLocalizedText(entry, 'name', lang);
    if (id === null || name === null) continue;
    const items = mapItems(toArray(entry['items']), id, lang);
    // Пустая группа выбирать нечего, а с `required` она бы намертво заблокировала добавление товара.
    if (items.length === 0) continue;
    groups.push({
      id,
      name,
      maxSelect: toLimit(entry['maxSelect']),
      required: entry['required'] === true,
      items,
    });
  }
  return groups;
}

function mapVariants(raw: unknown[], lang: AppLang): ProductVariant[] {
  const variants: ProductVariant[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) continue;
    const id = toId(entry['id']);
    const name = toLocalizedText(entry, 'name', lang);
    const price = toNumber(entry['price']);
    if (id === null || name === null || price === null) continue;
    variants.push({ id, name, price });
  }
  return variants;
}

function mapGroupIds(raw: unknown[], knownGroupIds: ReadonlySet<string>): string[] {
  const ids = new Set<string>();
  for (const entry of raw) {
    const id = toId(entry);
    // Ссылка на несуществующую группу нарисовала бы пустой блок, повтор — один и тот же блок дважды.
    if (id !== null && knownGroupIds.has(id)) ids.add(id);
  }
  return [...ids];
}

function mapProducts(raw: unknown[], knownGroupIds: ReadonlySet<string>, lang: AppLang): Product[] {
  const products: Product[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) continue;
    const id = toId(entry['id']);
    const categoryId = toId(entry['categoryId']);
    const name = toLocalizedText(entry, 'name', lang);
    const price = toNumber(entry['price']);
    // Товар без id, названия или цены чинить нечем; категория необязательна — бэкенд шлёт и null.
    if (id === null || name === null || price === null) continue;
    const variants = mapVariants(toArray(entry['variants']), lang);
    products.push({
      id,
      categoryId,
      name,
      description: toLocalizedText(entry, 'description', lang),
      price,
      // Дефолтный вариант сервер не отмечает — берём первый (docs/plan-api.md § 1.1).
      defaultVariantId: variants.length > 0 ? variants[0].id : null,
      imageUrl: toUrl(entry['imageUrl']),
      variants,
      modificationGroupIds: mapGroupIds(toArray(entry['modificationGroupIds']), knownGroupIds),
      unit: toLocalizedText(entry, 'unit', lang),
      isWeight: entry['isWeight'] === true,
      // Источника на бэкенде нет, UI эти поля прячет при null.
      rating: null,
      cookTimeMin: null,
      weightG: null,
    });
  }
  return products;
}

/** Порядок задаёт `sortOrder`, а не позиция в ответе; sort стабилен — равные остаются как пришли. */
export function mapBanners(raw: unknown, lang: AppLang): Banner[] {
  const banners: Banner[] = [];
  for (const entry of toArray(raw)) {
    if (!isRecord(entry)) continue;
    const id = toId(entry['id']);
    // БЕЗ fallback на другой язык (в отличие от каталога): баннер — дизайн-контент на своём языке,
    // `imageUrlRu` необязателен — такой баннер в ru просто не показывается.
    const imageUrl = toUrl(entry[lang === 'ru' ? 'imageUrlRu' : 'imageUrlUz']);
    if (id === null || imageUrl === null) continue;
    // Без `sortOrder` — в конец: любое малое число вставило бы такой баннер раньше явных 10, 20, 30.
    banners.push({
      id,
      imageUrl,
      sortOrder: toNumber(entry['sortOrder']) ?? Number.MAX_SAFE_INTEGER,
    });
  }
  return banners.sort((left, right) => left.sortOrder - right.sortOrder);
}

/** Ответ недоверенный: всё, что не прошло проверку по типу, из каталога выбрасывается, а не «чинится». */
export function mapCatalog(raw: unknown, lang: AppLang): Catalog {
  // Пустой каталог — это три пустых массива; не-объект значит сломанный ответ, а не «товаров нет».
  if (!isRecord(raw)) {
    throw new ApiError(null, 'unknown');
  }
  const modificationGroups = mapGroups(toArray(raw['modificationGroups']), lang);
  const knownGroupIds = new Set(modificationGroups.map((group) => group.id));
  return {
    categories: mapCategories(toArray(raw['categories']), lang),
    products: mapProducts(toArray(raw['positions']), knownGroupIds, lang),
    modificationGroups,
  };
}

/** Граница доверия: процент — конечное число в (0; 100]; "10", 0, 150, null — не акция. */
function toPercent(raw: unknown): number | null {
  const amount = isRecord(raw) ? toNumber(raw['amount']) : null;
  return amount !== null && amount > 0 && amount <= 100 ? amount : null;
}

/** `response: null` (unwrap → undefined) — «акций нет», не ошибка; битые записи — по одной. */
export function mapPromos(raw: unknown): Promos {
  if (!isRecord(raw)) {
    return NO_PROMOS;
  }
  const defaultPercent = toPercent(raw['defaultPromo']);
  // Общая акция перекрывает positions целиком — их даже не читаем.
  if (defaultPercent !== null) {
    return { defaultPercent, byId: new Map() };
  }
  const byId = new Map<string, number>();
  const positions = raw['positions'];
  if (isRecord(positions)) {
    for (const [id, promo] of Object.entries(positions)) {
      const percent = toPercent(promo);
      if (id.trim() !== '' && percent !== null) {
        byId.set(id, percent);
      }
    }
  }
  return byId.size === 0 ? NO_PROMOS : { defaultPercent: null, byId };
}
